import { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronRight, Gift } from 'lucide-react'

const STEPS = [
  {
    emoji: '🔮',
    title: 'ทำนายฝัน',
    desc: 'ตอบ 3 คำถามสั้นๆ → AI ตีความฝันให้ + เลขมงคล 3 ตัว',
    bullet: ['ฝันเห็นอะไร?', 'รู้สึกอย่างไร?', 'อยู่ที่ไหน?'],
  },
  {
    emoji: '✨',
    title: 'ดูดวงรายวัน',
    desc: 'ฟรีทุกหมวด — ผลส่วนตัวจากวันเกิดของคุณ',
    bullet: ['💼 การงาน', '💰 การเงิน', '❤️ ความรัก', '🌿 สุขภาพ'],
  },
  {
    emoji: '🔥',
    title: 'เช็คอินทุกวัน',
    desc: 'รับตั๋วฟรี + ต่อ Streak ลุ้น Bonus สูงสุด 5 ตั๋วต่อรอบ',
    bullet: ['🌱 3 วัน +1 ตั๋ว', '⭐ 7 วัน +2 ตั๋ว', '💎 14 วัน +3 ตั๋ว', '👑 30 วัน +5 ตั๋ว'],
  },
] as const

export default function OnboardingModal({
  onComplete,
  isClaiming,
}: {
  onComplete: (refCode?: string) => void
  isClaiming: boolean
}) {
  const [step, setStep] = useState(0)
  const [refCode, setRefCode] = useState('')
  const isLast = step === STEPS.length - 1
  const current = STEPS[step]

  const sanitized = refCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)

  function handleFinal() {
    onComplete(sanitized || undefined)
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col"
         style={{
           background: 'var(--color-bg)',
           paddingBottom: 'env(safe-area-inset-bottom, 0px)',
         }}>
      {/* Skip top-right */}
      <div className="flex justify-end p-4">
        <button onClick={() => onComplete()} disabled={isClaiming}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-transform active:scale-95 disabled:opacity-50"
                style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
          {'ข้าม'} <X size={12} />
        </button>
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center overflow-y-auto">
        <div className="text-7xl mb-5">{current.emoji}</div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-gold-dark)' }}>
          {current.title}
        </h2>
        <p className="text-sm mb-6 max-w-xs" style={{ color: 'var(--color-text-muted)' }}>
          {current.desc}
        </p>
        <div className="w-full max-w-xs flex flex-col gap-2 mb-2">
          {current.bullet.map((b, i) => (
            <div key={i}
                 className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-left"
                 style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}>
              <ChevronRight size={14} style={{ color: 'var(--color-gold)' }} />
              <span className="flex-1">{b}</span>
            </div>
          ))}
        </div>

        {/* Ref code input — แสดงเฉพาะ step สุดท้าย */}
        {isLast && (
          <div className="w-full max-w-xs mt-5 pt-5 border-t" style={{ borderColor: 'var(--color-surface-3)' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gift size={14} style={{ color: 'var(--color-gold-dark)' }} />
              <span className="text-xs font-semibold" style={{ color: 'var(--color-text-muted)' }}>
                มีโค้ดเพื่อนชวน? (ไม่บังคับ)
              </span>
            </div>
            <input
              type="text"
              value={refCode}
              onChange={e => setRefCode(e.target.value)}
              placeholder="เช่น ABCD23"
              inputMode="text"
              autoCapitalize="characters"
              maxLength={8}
              className="w-full px-4 py-2.5 rounded-xl text-center text-base font-mono tracking-widest font-bold outline-none"
              style={{
                background: 'var(--color-surface)',
                color: 'var(--color-gold-dark)',
                border: '1px dashed ' + (sanitized.length >= 4 ? 'var(--color-gold)' : 'var(--color-surface-3)'),
              }}
            />
            {sanitized.length > 0 && sanitized.length < 4 && (
              <p className="text-[10px] mt-1.5 text-center" style={{ color: 'var(--color-text-soft)' }}>
                {'โค้ดควรยาว 4-8 ตัวอักษร'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Dots + Action */}
      <div className="px-6 pb-8 pt-4">
        <div className="flex justify-center gap-2 mb-5">
          {STEPS.map((_, i) => (
            <div key={i} className="h-1.5 rounded-full transition-all"
                 style={{
                   width: i === step ? 24 : 6,
                   background: i === step ? 'var(--color-gold)' : 'var(--color-surface-3)',
                 }} />
          ))}
        </div>
        <button
          onClick={() => (isLast ? handleFinal() : setStep(s => s + 1))}
          disabled={isClaiming}
          className="w-full py-4 rounded-2xl font-bold text-base btn-gold shimmer disabled:opacity-60">
          {isClaiming
            ? 'กำลังเตรียมตั๋วให้คุณ...'
            : isLast ? '🎁 รับฟรี 3 ตั๋ว เริ่มเลย!' : 'ถัดไป'}
        </button>
        <p className="text-center text-[10px] mt-3" style={{ color: 'var(--color-text-soft)' }}>
          {isLast ? 'พร้อมใช้งานทันทีหลังกดยืนยัน' : `ขั้นตอนที่ ${step + 1} / ${STEPS.length}`}
        </p>
      </div>
    </div>,
    document.body
  )
}
